import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { confirmDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellPlate, CellMoney } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { followJob } from '../../../services/followJob';
import { Pill } from './ui';
import { useRowLinks, stickyTop } from './jobLinks';

// Registering fines on Ejar - ONE route (owner, 1 Oct 2026: "ليه منسجلش كله يدوي بدل زرين ومنطقين"): every fine
// by hand on its contract's screen (Add Traffic Violation), the way an employee does it. Opened from the fines page
// (its filters) and from The Engine's customer tab (one contract's fines, `tickets`). The engine previews from its
// database (no Ejar call), checks each fine on its contract in Ejar right before writing it, writes under the
// signed-in user's own Ejar login, and reports each fine's verdict from re-reading its contract on Ejar.

const TONE = { registered: 'green', 'wrong-amount': 'amber', 'not-found': 'red', failed: 'red', queued: 'gray', sent: 'indigo', already: 'indigo' };
const LABEL = { registered: 'On Ejar', 'wrong-amount': 'On Ejar, wrong amount', 'not-found': 'Not found after sending', failed: 'Failed', queued: 'Waiting', sent: 'Sent', already: 'Already there' };

// The fine's amount + its impound line + the service charge of its black points (AED 350 + VAT, owner 1 Oct 2026);
// a "lines only" row (the fine is on Ejar already, its impound / service charge line is not - audit FIN-06) carries
// those lines alone.
const AmountCell = ({ r }) => {
    const extra = (r.impound ? [<span key="i" title="vehicle impound"> + <CellMoney value={Number(r.impound)} currency="AED" /></span>] : [])
        .concat(r.service ? [<span key="s" title={`service charge for ${r.blackPoints || ''} black points`.replace('  ', ' ')}> + <CellMoney value={Number(r.service)} currency="AED" /></span>] : []);
    return r.linesOnly
        ? <><CellMoney value={Number(r.impound || 0) + Number(r.service || 0)} currency="AED" /> <Pill tone="amber">{r.impound && r.service ? 'impound + service charge only' : r.impound ? 'impound only' : 'service charge only'}</Pill></>
        : <><CellMoney value={Number(r.amount || 0)} currency="AED" />{extra}</>;
};

const FinesToRegister = ({ section, selected, onToggle, onToggleAll, onStart, disabled, FineLink, ContractLink }) => {
    const rows = section?.rows || [];
    const total = rows.filter((r) => selected.has(r.ticket)).reduce((s, r) => s + (r.amount || 0) + (r.impound || 0) + (r.service || 0), 0);
    const allChecked = rows.length > 0 && selected.size === rows.length;
    const anyNote = rows.some((r) => r.why);
    return (
        <Card
            className="mb-4"
            title="Fines to register"
            subtitle="Each fine is added on its contract's screen in Ejar, one at a time, with its impound and black points' service charge lines - nothing Ejar holds already goes twice."
            actions={(
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <span>{selected.size} / {rows.length} selected · {fmtAED(total)} · {section?.contracts || 0} contract(s)</span>
                    <Button variant="primary" size="sm" disabled={disabled || selected.size === 0} onClick={onStart}
                            leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}>
                        Register on Ejar
                    </Button>
                </div>
            )}
        >
            {rows.length === 0 ? <div className="text-sm text-gray-500">Nothing to register - every fine here is on Ejar already, or left out below.</div> : (
                <TableContainer maxHeight={360}>
                    <Table sticky>
                        <TableHead><TableRow>
                            <TableHeader className="w-8">
                                <input type="checkbox" checked={allChecked} onChange={() => onToggleAll(rows)}
                                       aria-label={allChecked ? 'Clear selection' : 'Select all fines'} />
                            </TableHeader>
                            <TableHeader>#</TableHeader><TableHeader>Ticket</TableHeader><TableHeader>Plate</TableHeader><TableHeader>Date</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Customer</TableHeader><TableHeader>Amount</TableHeader>{anyNote && <TableHeader>Note</TableHeader>}
                        </TableRow></TableHead>
                        <TableBody>{rows.map((r, i) => (
                            <TableRow key={r.ticket}>
                                <TableCell><input type="checkbox" checked={selected.has(r.ticket)} onChange={() => onToggle(r.ticket)} aria-label={`Select fine ${r.ticket}`} /></TableCell>
                                <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                <TableCell><FineLink ticket={r.ticket} /></TableCell><TableCell><CellPlate>{r.plate}</CellPlate></TableCell><TableCell><span className="ds-cell-date">{r.date}</span></TableCell>
                                <TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.plate} /></TableCell><TableCell><CellText>{r.customer}</CellText></TableCell>
                                <TableCell className="whitespace-nowrap"><AmountCell r={r} /></TableCell>
                                {anyNote && <TableCell><CellText>{r.why}</CellText></TableCell>}
                            </TableRow>))}</TableBody>
                    </Table>
                </TableContainer>
            )}
        </Card>
    );
};

const JobView = ({ job, onCancel, onBack, FineLink, ContractLink }) => {
    const rows = Object.values(job.results || {});
    const s = job.summary || {};
    const running = job.status === 'running';
    const headline = running ? 'Registering on Ejar…' : job.status === 'done' ? 'Finished' : job.status === 'cancelled' ? 'Stopped' : 'Failed';
    return (
        <div>
            <div className={`${stickyTop} flex flex-wrap items-center justify-between gap-2 mb-3 text-sm`}>
                <div aria-live="polite">
                    <span className="inline-flex items-center gap-2 font-semibold text-gray-900">{running && <LoadingSpinner size="sm" />}{headline}</span>
                    <span className="text-gray-600"> · {job.done} / {job.total} · as {job.ejarUser}</span>
                    <div className="text-xs text-gray-500">{job.stage}{job.error ? ` · ${job.error}` : ''}</div>
                </div>
                <div className="flex gap-2">
                    {running && <Button size="sm" variant="danger" leadingIcon={<Icon glyph={ICONS.close} size="sm" />} onClick={onCancel}>Stop</Button>}
                    {!running && <Button size="sm" leadingIcon={<Icon glyph={ICONS.arrowLeft} size="sm" />} onClick={onBack}>Back to the list</Button>}
                </div>
            </div>
            {!running && (
                <div className="text-sm mb-3 flex flex-wrap gap-2">
                    {['registered', 'wrong-amount', 'not-found', 'failed'].filter((k) => s[k]).map((k) => <Pill key={k} tone={TONE[k]}>{LABEL[k]}: {s[k]}</Pill>)}
                </div>
            )}
            <TableContainer maxHeight={520}>
                <Table sticky>
                    <TableHead><TableRow><TableHeader>#</TableHeader><TableHeader>Ticket</TableHeader><TableHeader>Plate</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Amount</TableHeader><TableHeader>Result</TableHeader><TableHeader>Detail</TableHeader></TableRow></TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={r.ticket}>
                                <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                <TableCell><FineLink ticket={r.ticket} /></TableCell>
                                <TableCell><CellPlate>{r.plate}</CellPlate></TableCell><TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.plate} /></TableCell>
                                <TableCell className="whitespace-nowrap"><AmountCell r={r} /></TableCell>
                                <TableCell><Pill tone={TONE[r.status] || 'gray'} title={r.ejarNote || ''}>{LABEL[r.status] || r.status}</Pill></TableCell>
                                <TableCell><CellText>{r.detail}</CellText></TableCell>
                            </TableRow>))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-gray-500">No results yet</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

/** `filters` - the fines page's filters; `tickets` - exact fines (The Engine: one contract's), which win. */
export default function EjarRegisterModal({ filters, tickets, onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const changed = useRef(false);   // something was sent: the table behind must refresh on close
    const { FineLink, ContractLink, modals } = useRowLinks();
    const askKey = JSON.stringify(tickets ? { tickets } : { filters: filters || {} });

    const load = async () => {
        setError(null); setPreview(null);
        try {
            const p = await finesService.ejarRegister({ commit: false, ...JSON.parse(askKey) });
            setPreview(p);
            setSel(new Set(p.fines.rows.map((r) => r.ticket)));
        } catch (e) { setError(errorMessage(e)); }
    };
    useEffect(() => { load(); }, [askKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (t) => setSel((s) => { const n = new Set(s); if (n.has(t)) n.delete(t); else n.add(t); return n; });
    const toggleAll = (rows) => setSel((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.ticket))));

    const start = async () => {
        const chosen = [...sel];
        if (!chosen.length) return;
        if (!(await confirmDialog({
            title: 'Register on Ejar?',
            message: `Add ${chosen.length} fine(s) on their contracts in Ejar, one by one, as ${preview.ejarUser}?`,
            confirmLabel: 'Register',
        }))) return;
        setBusy(true); setError(null);
        try {
            const started = await finesService.ejarRegister({ commit: true, tickets: chosen });
            changed.current = true;
            // A failed poll is retried (the job keeps running on the server): the dialog stays
            // busy until the job is finished, so the same fines cannot be sent twice.
            await followJob(() => finesService.ejarRegisterJob(started.jobId), {
                onUpdate: (j) => { setError(null); setJob(j); },
                onRetry: (e) => setError(`Lost contact with the engine (${errorMessage(e)}) - still following the job, retrying…`),
            });
        } catch (e) { setError(errorMessage(e)); }
        setBusy(false);
    };
    const cancel = async () => { if (job?.jobId) { try { await finesService.ejarRegisterCancel(job.jobId); } catch (e) { setError(errorMessage(e)); } } };
    const close = () => { if (busy) return; if (changed.current) onDone?.(); onClose(); };

    return (
        <Dialog open onClose={close} size="xl" title="Register on Ejar" closeOnOverlay={!busy}>
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {!preview && !error && !job && <LoadingSpinner message="Reading the fines…" />}
            {preview && !job && (
                <>
                    <div className="text-sm text-gray-600 mb-3">
                        {preview.mustConfigure
                            ? <span className="text-red-700 font-semibold">Save your own Ejar login first (The Engine → Ejar account): every entry on Ejar is made under the user who enters it.</span>
                            : <>Entries go on Ejar as <b>{preview.ejarUser}</b>{preview.ejarOwnAccount ? '' : ' (server account)'}. Nothing is sent until you confirm; the result of every fine is read back from Ejar.</>}
                    </div>
                    <FinesToRegister section={preview.fines} selected={sel} onToggle={toggle} onToggleAll={toggleAll} onStart={start} disabled={preview.mustConfigure || busy} FineLink={FineLink} ContractLink={ContractLink} />
                    {preview.left?.length > 0 && (
                        <details className="text-sm mt-1"><summary className="cursor-pointer text-gray-600">{preview.left.length} fine(s) left out</summary>
                            <ul className="mt-1 ml-4 list-disc text-gray-600">{preview.left.map((x) => <li key={x.ticket}><span className="font-mono">{x.ticket}</span>: {x.why}</li>)}</ul></details>
                    )}
                </>
            )}
            {job && <JobView job={job} onCancel={cancel} onBack={() => { setJob(null); load(); }} FineLink={FineLink} ContractLink={ContractLink} />}
            {modals}
        </Dialog>
    );
}
